// Gráficas de la pestaña «Gasto»: la barra del objetivo del titular, el gasto por hora de un día (solo las horas
// registradas; lo anterior, rayado «sin registro») y el % de nuestra parte que se come cada día (columnas apiladas por
// partida; lo estimado, en un solo gris rayado). SVG propio dibujado al ancho medido: el texto sale a 12 px reales.
import { useId } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { dineroExacto, numero } from './formato';
import { Etiqueta, FOCO, franjaHora, usePuntero, type FilaEtiqueta } from './Puntero';
import {
  agrupar,
  estadoDe,
  fecha,
  FONDO_ESTADO,
  GRUPOS,
  hora,
  OBJETIVO,
  pct,
  pctDia,
  TOPE_BARRA,
  usd,
  type DiaGasto,
} from './logicaGasto';
import { useTamano } from './useTamano';

const IZQ = 44;
const DER = 8;
const ARRIBA = 24;
const ABAJO = 28;
const EJE = 'fill-oficina-texto3 text-o-nota cifras';
const VALOR = 'fill-oficina-texto2 text-o-nota font-medium cifras';

/** Barra fina de 0 a 40 % con la marca del objetivo (20 %) en medio. */
export function BarraObjetivo({ p, className }: { p: number | null; className?: string }) {
  const ancho = p === null ? 0 : Math.min(Math.max(p / TOPE_BARRA, 0), 1) * 100;
  const marca = (OBJETIVO / TOPE_BARRA) * 100;
  return (
    <div className={cn('mt-3', className)} role="img" aria-label={t('salaControl.gasto.barraAria', { pct: pct(p) })}>
      <div className="relative h-2 rounded bg-oficina-linea">
        <div className={cn('h-2 rounded', FONDO_ESTADO[estadoDe(p)])} style={{ width: `${ancho}%` }} />
        <div className="absolute -top-1 h-4 w-0.5 -translate-x-1/2 rounded-sm bg-oficina-texto" style={{ left: `${marca}%` }} />
      </div>
      <div className="relative mt-2 flex justify-between text-o-nota leading-4 text-oficina-texto3 cifras">
        <span>0 %</span>
        <span className="absolute -translate-x-1/2 font-medium text-oficina-texto2" style={{ left: `${marca}%` }}>
          {t('salaControl.gasto.barraObjetivo')}
        </span>
        <span>{numero(TOPE_BARRA * 100)} %</span>
      </div>
    </div>
  );
}

/** Techo redondo del eje (92 → 100; 230 → 250). */
export function techoRedondo(max: number): number {
  if (max <= 0) return 10;
  const paso = 10 ** Math.floor(Math.log10(max));
  for (const f of [1, 2, 2.5, 5, 10]) if (f * paso >= max * 1.05) return f * paso;
  return 10 * paso;
}

function Patron({ id }: { id: string }) {
  return (
    <pattern id={id} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width="1.5" height="6" className="fill-oficina-fondo" fillOpacity={0.45} />
    </pattern>
  );
}

function useLienzo(anchoPorDefecto: number) {
  const [ref, tam] = useTamano<HTMLDivElement>();
  return [ref, tam?.ancho ?? anchoPorDefecto] as const;
}

/** Gasto por hora de un día: barras grises (el servidor no reparte las horas por partida); las horas estimadas, en el
 *  mismo gris rayado que los días estimados; la zona rayada «sin registro» antes de la primera hora con datos (si no se
 *  estimó) y el hueco «en curso» de la hora que se está apuntando. */
export function HorasRegistradas({
  porHora,
  primera,
  ultima,
  enCurso,
  estimadasAntesDe = 0,
  alto,
}: {
  porHora: number[];
  /** Horas [0, N) estimadas (sin registro llamada a llamada): van rayadas. 24 = todo el día estimado. */
  estimadasAntesDe?: number;
  /** Primera hora con registro (0 = día completo). */
  primera: number;
  /** Última hora apuntada. */
  ultima: number;
  /** Pinta la hora siguiente como «en curso» (hoy). */
  enCurso: boolean;
  alto: number;
}) {
  const [ref, ancho] = useLienzo(568);
  const pid = `rayas-${useId().replace(/:/g, '')}`;
  const horas: number[] = [];
  for (let h = primera; h <= ultima; h++) horas.push(h);
  const curso = enCurso && ultima < 23 ? ultima + 1 : null;
  const huecos = horas.length + (curso !== null ? 1 : 0);
  const plot = ancho - IZQ - DER;
  const zona = primera > 0 ? Math.min(200, Math.max(120, plot * 0.3)) : 0;
  const xIni = IZQ + zona + (zona ? 8 : 0);
  const slot = (ancho - DER - xIni) / Math.max(huecos, 1);
  const bw = Math.min(56, slot * 0.6);
  const max = techoRedondo(Math.max(0, ...horas.map((h) => porHora[h] ?? 0)));
  const h = alto - ARRIBA - ABAJO;
  const y = (v: number) => ARRIBA + h * (1 - v / max);
  const pocas = huecos <= 12;
  const conEtiqueta = (hr: number, i: number) => pocas || hr % 3 === 0 || i === 0;
  const totalDia = horas.reduce((s, hr) => s + (porHora[hr] ?? 0), 0);
  const cxDe = (i: number) => xIni + slot * i + slot / 2;
  const p = usePuntero({
    n: huecos,
    ancho,
    indiceDe: (px) => {
      const i = Math.floor((px - xIni) / slot);
      return px >= xIni && i >= 0 && i < huecos ? i : null;
    },
  });
  const sobre = p.i;
  const etiqueta = (() => {
    if (sobre === null) return null;
    if (sobre >= horas.length) return { titulo: franjaHora(curso ?? 0), filas: [], pie: t('salaControl.grafica.enCurso') };
    const hr = horas[sobre]!;
    const v = porHora[hr] ?? 0;
    const filas: FilaEtiqueta[] = [{ etiqueta: t('salaControl.grafica.gasto'), valor: dineroExacto(v), marca: 'bg-oficina-texto2', forma: 'barra' }];
    if (totalDia > 0) filas.push({ etiqueta: t('salaControl.grafica.delDia'), valor: `${numero((100 * v) / totalDia, 1)} %`, tenue: true });
    return { titulo: franjaHora(hr), filas, pie: hr < estimadasAntesDe ? t('salaControl.grafica.estimado') : undefined };
  })();
  return (
    <div ref={ref} className="relative w-full" style={{ height: alto }}>
      <div {...p.props} className={cn('relative h-full', FOCO)} aria-label={t('salaControl.gasto.porHoraAria')}>
      <svg width={ancho} height={alto} viewBox={`0 0 ${ancho} ${alto}`} role="img" aria-label={t('salaControl.gasto.porHoraAria')} className="block overflow-visible">
        <defs>
          <Patron id={pid} />
        </defs>
        {sobre !== null ? <rect x={cxDe(sobre) - slot / 2 + 1} y={ARRIBA - 6} width={slot - 2} height={h + 6} rx={6} className="fill-oficina-hover" /> : null}
        {[0, max / 2, max].map((v) => (
          <g key={v}>
            <text x={IZQ - 10} y={y(v) + 4} textAnchor="end" className={EJE}>
              {`${numero(v, v % 1 ? 1 : 0)} $`}
            </text>
            <line x1={IZQ} x2={ancho - DER} y1={y(v)} y2={y(v)} className="stroke-oficina-linea" />
          </g>
        ))}
        {zona ? (
          <g>
            <rect x={IZQ} y={ARRIBA} width={zona} height={h} rx={6} className="fill-oficina-hover" />
            <rect x={IZQ} y={ARRIBA} width={zona} height={h} rx={6} fill={`url(#${pid})`} opacity={0.9} />
            <text x={IZQ + zona / 2} y={ARRIBA + h / 2 - 4} textAnchor="middle" className={VALOR}>
              {t('salaControl.gasto.sinRegistro')}
            </text>
            <text x={IZQ + zona / 2} y={ARRIBA + h / 2 + 14} textAnchor="middle" className={EJE}>
              {`00:00 – ${String(primera - 1).padStart(2, '0')}:59`}
            </text>
            <text x={IZQ + 4} y={alto - 8} className={EJE}>
              00:00
            </text>
          </g>
        ) : null}
        {horas.map((hr, i) => {
          const v = porHora[hr] ?? 0;
          const cx = cxDe(i);
          const estimada = hr < estimadasAntesDe;
          const alta = Math.max(0, y(0) - y(v));
          const marcada = sobre === i;
          return (
            <g key={hr} data-testid={estimada ? 'hora-estimada' : 'hora-registrada'}>
              <rect
                x={cx - bw / 2}
                y={y(v)}
                width={bw}
                height={alta}
                rx={3}
                className={cn(estimada ? 'fill-oficina-serie-5' : marcada ? 'fill-oficina-texto' : 'fill-oficina-texto2', 'transition-[fill] duration-100')}
              />
              {estimada ? <rect x={cx - bw / 2} y={y(v)} width={bw} height={alta} rx={3} fill={`url(#${pid})`} /> : null}
              {pocas ? (
                <text x={cx} y={y(v) - 8} textAnchor="middle" className={VALOR}>
                  {usd(v)}
                </text>
              ) : null}
              {conEtiqueta(hr, i) || marcada ? (
                <text x={cx} y={alto - 8} textAnchor="middle" className={cn(EJE, marcada && 'fill-oficina-texto font-semibold')}>
                  {hora(hr)}
                </text>
              ) : null}
            </g>
          );
        })}
        {curso !== null ? (
          <g>
            <rect
              x={cxDe(horas.length) - bw / 2}
              y={y(max * 0.3)}
              width={bw}
              height={y(0) - y(max * 0.3)}
              rx={4}
              fill="none"
              strokeWidth={1.5}
              strokeDasharray="3 3"
              className="stroke-oficina-gasto-hueco"
            />
            {/* Con muchas horas el hueco es estrecho: el texto va arriba, pegado al borde, para no pisar la última barra. */}
            <text
              x={pocas ? cxDe(horas.length) : ancho - DER}
              y={pocas ? y(max * 0.3) - 8 : ARRIBA - 8}
              textAnchor={pocas ? 'middle' : 'end'}
              className={EJE}
            >
              {t('salaControl.gasto.enCurso')}
            </text>
            {pocas || curso % 3 === 0 ? (
              <text x={cxDe(horas.length)} y={alto - 8} textAnchor="middle" className={EJE}>
                {hora(curso)}
              </text>
            ) : null}
          </g>
        ) : null}
      </svg>
      {etiqueta && p.activa ? <Etiqueta x={cxDe(sobre!)} ancho={ancho} arriba={ARRIBA} titulo={etiqueta.titulo} filas={etiqueta.filas} pie={etiqueta.pie} /> : null}
      </div>
    </div>
  );
}

type TipoDia = 'vacio' | 'curso' | 'estimado' | 'registro';
function tipoDe(d: DiaGasto | null, dia: string, hoy: string): TipoDia {
  if (dia === hoy && d) return 'curso';
  if (!d || pctDia(d) === null) return 'vacio';
  return d.origen === 'estimado' ? 'estimado' : 'registro';
}

/** Una columna por día: su altura es el % de nuestra parte que se come el gasto. Con registro, apilada por partida;
 *  estimada, un solo gris rayado (su reparto es aproximado); sin datos, un hueco discontinuo; hoy, «en curso»
 *  (un día a medias no se compara con días enteros). */
export function ColumnasPorcentajeDia({
  dias,
  hoy,
  resaltar,
  alto = 296,
}: {
  dias: Array<{ dia: string; d: DiaGasto | null }>;
  hoy: string;
  /** Día elegido (p. ej. «Ayer» frente a su semana): el resto de estimados se apaga. */
  resaltar?: string;
  alto?: number;
}) {
  const [ref, ancho] = useLienzo(568);
  const pid = `rayas-${useId().replace(/:/g, '')}`;
  const n = Math.max(dias.length, 1);
  const tipos = dias.map((x) => tipoDe(x.d, x.dia, hoy));
  const valores = dias.map((x, i) => (tipos[i] === 'estimado' || tipos[i] === 'registro' ? pctDia(x.d!) : null));
  const top = Math.max(TOPE_BARRA, Math.ceil(Math.max(0, ...valores.map((v) => v ?? 0)) / 0.2) * 0.2);
  const h = alto - ARRIBA - ABAJO;
  const y = (v: number) => ARRIBA + h * (1 - Math.min(v, top) / top);
  const slot = (ancho - IZQ - DER) / n;
  const bw = Math.min(56, slot * 0.62);
  const cx = (i: number) => IZQ + slot * i + slot / 2;
  const conValor = n <= 14;
  const marcas: number[] = [];
  for (let v = 0; v <= top + 1e-9; v += 0.2) marcas.push(Math.round(v * 100) / 100);
  // Etiquetas del eje X: todas hasta 14 días; si no, cada 5 y la última (sin pisarse).
  const etiquetaX = (i: number) => n <= 14 || i === n - 1 || dias[i]!.dia === resaltar || (i % 5 === 0 && n - 1 - i >= 3);

  // Huecos: los días seguidos sin datos van en un solo rectángulo con su texto.
  const huecos: Array<[number, number]> = [];
  tipos.forEach((tp, i) => {
    if (tp !== 'vacio') return;
    const ult = huecos[huecos.length - 1];
    if (ult && ult[1] === i - 1) ult[1] = i;
    else huecos.push([i, i]);
  });

  const p = usePuntero({
    n: dias.length,
    ancho,
    indiceDe: (px) => (px >= IZQ && px <= ancho - DER ? Math.min(dias.length - 1, Math.max(0, Math.floor((px - IZQ) / slot))) : null),
  });
  const sobre = p.i;

  return (
    <div ref={ref} className="relative w-full" style={{ height: alto }}>
      <div {...p.props} className={cn('relative h-full', FOCO)} aria-label={t('salaControl.gasto.porDiaAria')}>
      <svg width={ancho} height={alto} viewBox={`0 0 ${ancho} ${alto}`} role="img" aria-label={t('salaControl.gasto.porDiaAria')} className="block overflow-visible">
        <defs>
          <Patron id={pid} />
        </defs>
        {sobre !== null ? <rect x={IZQ + slot * sobre + 1} y={ARRIBA - 6} width={slot - 2} height={h + 6} rx={6} className="fill-oficina-hover" /> : null}
        {marcas.map((v) => (
          <g key={v}>
            {Math.abs(v - OBJETIVO) > 1e-9 ? <line x1={IZQ} x2={ancho - DER} y1={y(v)} y2={y(v)} className="stroke-oficina-linea" /> : null}
            {Math.abs(v - OBJETIVO) > 1e-9 ? (
              <text x={IZQ - 10} y={y(v) + 4} textAnchor="end" className={EJE}>
                {pct(v)}
              </text>
            ) : null}
          </g>
        ))}
        {huecos.map(([a, b]) => {
          const x0 = cx(a) - bw / 2;
          const x1 = cx(b) + bw / 2;
          const texto =
            a === b
              ? [t('salaControl.gasto.sinDatosCorto')]
              : [t('salaControl.gasto.sinDatosTramo', { desde: fecha(dias[a]!.dia), hasta: fecha(dias[b]!.dia) })];
          const partido = a === b ? texto[0]!.split(' ') : texto;
          return (
            <g key={a}>
              <rect x={x0} y={y(0.12)} width={x1 - x0} height={y(0) - y(0.12)} rx={4} fill="none" strokeWidth={1.5} strokeDasharray="3 3" className="stroke-oficina-gasto-hueco" />
              {x1 - x0 >= 24
                ? partido.map((l, k) => (
                    <text key={l} x={(x0 + x1) / 2} y={y(0.06) + 4 + 14 * k - 7 * (partido.length - 1)} textAnchor="middle" className={EJE}>
                      {l}
                    </text>
                  ))
                : null}
            </g>
          );
        })}
        {dias.map((x, i) => {
          const tp = tipos[i]!;
          const c = cx(i);
          const x0 = c - bw / 2;
          const v = valores[i] ?? null;
          const elegido = x.dia === resaltar;
          return (
            <g key={x.dia}>
              {etiquetaX(i) || sobre === i ? (
                <text x={c} y={alto - 8} textAnchor="middle" className={cn(EJE, elegido && 'fill-oficina-texto font-semibold')}>
                  {fecha(x.dia)}
                </text>
              ) : null}
              {tp === 'curso' ? (
                <g>
                  <rect x={x0} y={y(0.12)} width={bw} height={y(0) - y(0.12)} rx={4} fill="none" strokeWidth={1.5} strokeDasharray="3 3" className="stroke-oficina-gasto-hueco" />
                  {conValor ? (
                    <text x={c} y={y(0.12) - 8} textAnchor="middle" className={EJE}>
                      {t('salaControl.gasto.enCurso')}
                    </text>
                  ) : null}
                </g>
              ) : null}
              {tp === 'estimado' && v !== null ? (
                <g>
                  <rect
                    x={x0}
                    y={y(v)}
                    width={bw}
                    height={y(0) - y(v)}
                    rx={2}
                    className={resaltar && !elegido ? 'fill-oficina-gasto-apagado' : 'fill-oficina-serie-5'}
                  />
                  <rect x={x0} y={y(v)} width={bw} height={y(0) - y(v)} rx={2} fill={`url(#${pid})`} />
                </g>
              ) : null}
              {tp === 'registro' && v !== null ? <Apilada d={x.d!} x={x0} ancho={bw} y={y} /> : null}
              {sobre === i && v !== null ? (
                <rect x={x0 - 3} y={y(v) - 3} width={bw + 6} height={y(0) - y(v) + 3} rx={4} fill="none" strokeWidth={1.5} className="stroke-oficina-texto" />
              ) : null}
              {conValor && v !== null ? (
                <text x={c} y={y(v) - 8} textAnchor="middle" className={cn(VALOR, elegido && 'fill-oficina-texto font-semibold')}>
                  {pct(v)}
                </text>
              ) : null}
              <rect x={IZQ + slot * i} y={ARRIBA} width={slot} height={h} fill="transparent" data-testid={`columna-${x.dia}`} />
            </g>
          );
        })}
        <line x1={IZQ} x2={ancho - DER} y1={y(OBJETIVO)} y2={y(OBJETIVO)} strokeWidth={1.5} strokeDasharray="4 4" className="stroke-oficina-texto2" />
        <text x={IZQ - 10} y={y(OBJETIVO) + 4} textAnchor="end" className={cn(EJE, 'fill-oficina-texto2 font-semibold')}>
          {pct(OBJETIVO)}
        </text>
      </svg>
      {sobre !== null && p.activa ? <EtiquetaDia x={cx(sobre)} ancho={ancho} dia={dias[sobre]!.dia} d={dias[sobre]!.d} tp={tipos[sobre]!} /> : null}
      </div>
    </div>
  );
}

/** Etiqueta de un día: el % que se come, lo gastado y nuestra parte exactos y, con registro, el reparto por partida. */
function EtiquetaDia({ x, ancho, dia, d, tp }: { x: number; ancho: number; dia: string; d: DiaGasto | null; tp: TipoDia }) {
  const filas: FilaEtiqueta[] = [];
  let pie: string | undefined;
  if (!d || tp === 'vacio') filas.push({ etiqueta: t('salaControl.grafica.gasto'), valor: t('salaControl.grafica.sinDato'), tenue: true });
  else {
    const p = pctDia(d);
    if (tp !== 'curso' && p !== null) filas.push({ etiqueta: t('salaControl.grafica.seCome'), valor: `${numero(p * 100, 1)} %` });
    filas.push({ etiqueta: t('salaControl.grafica.gastado'), valor: dineroExacto(d.total), tenue: tp !== 'curso' });
    if (d.parte !== null) filas.push({ etiqueta: t('salaControl.grafica.nuestraParte'), valor: dineroExacto(d.parte), tenue: true });
    if (tp === 'registro') {
      const g = agrupar(d.partidas);
      let primera = true;
      for (const gr of GRUPOS) {
        if (g[gr.clave] <= 0) continue;
        filas.push({ etiqueta: t(`salaControl.gasto.grupos.${gr.clave}` as TKey), valor: dineroExacto(g[gr.clave]), marca: gr.clase, forma: 'barra', tenue: true, separada: primera });
        primera = false;
      }
    }
    pie = tp === 'curso' ? t('salaControl.gasto.tipEnCurso') : tp === 'estimado' ? t('salaControl.grafica.estimado') : undefined;
  }
  return <Etiqueta x={x} ancho={ancho} arriba={ARRIBA} titulo={fecha(dia)} filas={filas} pie={pie} />;
}

function Apilada({ d, x, ancho, y }: { d: DiaGasto; x: number; ancho: number; y: (v: number) => number }) {
  const g = agrupar(d.partidas);
  const parte = d.parte ?? 1;
  let base = 0;
  return (
    <>
      {GRUPOS.map((gr) => {
        const frac = g[gr.clave] / parte;
        if (frac <= 0) return null;
        const y1 = y(base + frac);
        const y0 = y(base);
        base += frac;
        return <rect key={gr.clave} x={x} y={y1 + 0.75} width={ancho} height={Math.max(y0 - y1 - 1.5, 0.5)} className={gr.fill} />;
      })}
    </>
  );
}
